'use client';

import * as React from 'react';
import classNames from 'classnames';
import './styles.scss';

function clamp(number: number, min: number, max: number) {
  return Math.max(min, Math.min(number, max));
}

export interface ILoadingContentProps {
  /** Layout to use */
  layout?: 'default' | 'absolute';
  /** Inline element size */
  size?: 'default' | 'tiny' | 'small' | 'medium';
  /** Label to display on larger sizes */
  label?: React.ReactNode;
  /** Display progress instead of spinner */
  value?: number;
  /** Range for value normalization */
  range?: {
    min: number;
    max: number;
  };
  /** ClassName */
  className?: string;
}

/**
 * Covers all while rendered. For example while loading content required for specific page.
 */
const LoadingContent: React.FunctionComponent<ILoadingContentProps> = (props) => {
  // Note double sizing for getting anti-alias by downsizing in the viewport
  const perc = 50;
  const containerWidth = 160 * 2;
  let strokeWidth = 7 * 2;
  if (props.size === 'tiny') {
    strokeWidth = 20 * 2;
  }
  if (props.size === 'small') {
    strokeWidth = 15 * 2;
  }

  const isProgressbar = typeof props.value !== 'undefined';
  const range = props.range || {
    min: 0,
    max: 100,
  };

  const radius = containerWidth / 2 - strokeWidth / 2;
  const circ = 2 * Math.PI * radius;
  const valOffset = (circ * clamp(perc, 0, 100)) / 100;
  const strokeDash = `${valOffset} ${circ - valOffset}`;
  const strokeOffset = circ / 2;

  const genSpec = (item: number) => {
    const value = ((item - range.min) / (range.max - range.min)) * 100;
    const valOffset = ((circ * clamp(value, 0, 100)) / 100) * 0.95;
    const strokeDash = `${valOffset} ${circ - valOffset}`;
    const strokeOffset = circ * 0.75;
    return {
      strokeDash,
      strokeOffset,
    };
  };

  const progressBarSpec = genSpec(props.value || 0);

  return (
    <div
      className={classNames(
        'loading-content',
        {
          // ['layout-default']: props.layout === 'default' || !props.layout,
          ['layout-absolute']: props.layout === 'absolute',
          ['size-default']: !props.size || props.size === 'default',
          ['size-tiny']: props.size === 'tiny',
          ['size-small']: props.size === 'small',
          ['size-medium']: props.size === 'medium',
          ['is-spinner']: !isProgressbar,
          ['is-progressbar']: isProgressbar,
        },
        props.className,
      )}
      aria-busy={true}
    >
      {props.label && props.size !== 'small' && props.size !== 'tiny' && <div className={'loading-content__label'}>{props.label}</div>}
      <svg
        viewBox={`0 0 ${containerWidth} ${containerWidth}`}
        preserveAspectRatio="xMinYMin slice"
      >
        {!isProgressbar && (
          <foreignObject
            x="0"
            y="0"
            width={containerWidth}
            height={containerWidth}
          >
            <div className={'loading-content__gradient'} />
          </foreignObject>
        )}
        <g className={'loading-content__group'}>
          <rect
            x="0"
            y="0"
            width={containerWidth}
            height={containerWidth}
            className={'loading-content__fill'}
          />
          <circle
            r={radius}
            cx={containerWidth / 2}
            cy={containerWidth / 2}
            strokeWidth={strokeWidth}
            fill="transparent"
            className={'loading-content__circle'}
          />
          <circle
            r={radius}
            cx={containerWidth / 2}
            cy={containerWidth / 2}
            strokeDasharray={isProgressbar ? progressBarSpec.strokeDash : strokeDash}
            strokeDashoffset={isProgressbar ? progressBarSpec.strokeOffset : strokeOffset}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeLinecap={'round'}
            className={'loading-content__elem'}
          />
        </g>
      </svg>
    </div>
  );
};

LoadingContent.displayName = 'LoadingContent';

export default LoadingContent;
